import { notify } from '@tiger/bugsnag';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { $Formutil, connect, Field } from 'react-bootstrap-formutil';
import Dialog from 'components/base/Dialog';
import Loading from 'components/Loading';
import Toast from 'components/Toast';
import ui, { getQueryValue } from 'stores/ui';
import API from 'utils/API';
import classlist from 'utils/classlist';
import { IS_DEV, IS_STAGING } from 'utils/constants';
import http from 'utils/http';
import { language } from 'utils/i18n';
import {
    CaptchaConfig,
    CaptchaErrorCode,
    CaptchaRequestType,
    CaptchaResponseType,
    CaptchaType,
    clearRecaptchaFailureRecord,
    GeeCaptchaObj,
    isRecaptchaAvailable,
    recaptchaByCheckbox,
    recaptchaByScore,
    recordRecaptchaFailure
} from './config';
import GeeSdk4 from './geeSdk4';

import './style.scss';

export { CaptchaType } from './config';

export interface CaptchaProps {
    $formutil: $Formutil<any>;
    useInlineLoading?: boolean;
    onSuccess?(): void;
    onError?(): void;
    onClose?(): void; // 关闭极验弹窗的回调
    isVoice?: boolean;
    action: string; // action 规则 https://tigertech.feishu.cn/wiki/wikcnmLmrcuharYooMW7BOOL61g#FwJvT3
}

const queryCaptchaType = getQueryValue('_debugCaptchaType');

const Captcha = forwardRef<{ reset(): void; captchaType?: CaptchaType }, CaptchaProps>(
    ({ action, useInlineLoading, $formutil, isVoice, onSuccess, onError, onClose }, ref) => {
        const boxRef = useRef<any>();
        const self = useRef<{
            captchaObj?: GeeCaptchaObj;
            widgetId?: number;
            onSuccess?(): void;
            onError?(): void;
            onClose?(): void;
            captchaType?: CaptchaType;
        }>({}).current;
        const [loading, setLoading] = useState(!!useInlineLoading);
        const [captchaType, setCaptchaType] = useState<CaptchaType>();

        self.onSuccess = onSuccess;
        self.onError = onError;
        self.onClose = onClose;

        const clearForm = () => {
            $formutil.$setValues({
                captcha_token: undefined
            });
        };

        const safetySetLoading = (loading: boolean) => {
            boxRef.current && setLoading(loading);
        };

        const reset = () => {
            if (self.captchaObj) {
                self.captchaObj.reset();
            }

            if (typeof self.widgetId !== 'undefined') {
                grecaptcha.enterprise.reset(self.widgetId);
            }

            clearForm();
        };

        const initCaptcha = async (captchaType?) => {
            const errorFallback = (message: string) => {
                Toast.loading(false);

                safetySetLoading(false);

                Dialog.alert(message);

                self.onError?.();
            };

            try {
                !useInlineLoading && Toast.loading(true, __('初始化验证中...'));

                /**
                 * 没有传入captchaType
                 * 测试环境或者staging环境
                 * query上指定了captchaType，并且captchaType合法
                 * recaptcha当前可用
                 * 同时满足以上四个条件，则可以用query上的captchaType
                 */
                if (
                    !captchaType &&
                    (IS_DEV || IS_STAGING) &&
                    queryCaptchaType &&
                    [CaptchaRequestType.GEETEST4, CaptchaRequestType.RECAPTCHA].includes(
                        queryCaptchaType as CaptchaRequestType
                    ) &&
                    isRecaptchaAvailable()
                ) {
                    captchaType = queryCaptchaType;
                }

                const { data: config } = await http.get<CaptchaConfig>(API.OAUTH.captchaInit(), {
                    params: {
                        captcha_type:
                            captchaType ??
                            (isRecaptchaAvailable()
                                ? `${CaptchaRequestType.GEETEST4},${CaptchaRequestType.RECAPTCHA}`
                                : CaptchaRequestType.GEETEST4),
                        ...(isVoice ? { type: 'voice' } : {})
                    }
                });

                if (!config) {
                    throw new Error(__('验证码初始化失败，请稍后刷新页面重试!'));
                }

                // 极验则直接渲染极验组件
                if (config.type === CaptchaResponseType.GEETEST4) {
                    self.captchaType = CaptchaType.GEETEST;

                    setCaptchaType(CaptchaType.GEETEST);

                    const { geetest4_sdk } = config;

                    GeeSdk4(
                        {
                            captchaId: geetest4_sdk!.captcha_id,
                            riskType: geetest4_sdk!.risk_type,
                            product: 'popup',
                            language: language.startsWith('en') ? 'eng' : 'zho',
                            rem: ui.isMobile ? 0.8 : 1,
                            nativeButton: {
                                width: '100%'
                            }
                        },
                        (captchaObj: GeeCaptchaObj) => {
                            self.captchaObj = captchaObj; // 保存下这个对象

                            captchaObj.appendTo(boxRef.current);

                            captchaObj
                                .onReady(() => {
                                    Toast.loading(false);

                                    captchaObj.showCaptcha();
                                })
                                .onSuccess(async () => {
                                    const result = captchaObj.getValidate();

                                    try {
                                        const { data } = await http.post(API.OAUTH.verifyGee(), {
                                            ...result,
                                            action
                                        });

                                        $formutil.$setValues(
                                            {
                                                captcha_token: data.captcha_token
                                            },
                                            () => {
                                                $formutil.$validate('captcha_token');
                                                self.onSuccess?.();
                                            }
                                        );
                                    } catch (error) {
                                        Toast.show(error);

                                        captchaObj.reset();
                                    }
                                })
                                .onError(() => {
                                    Toast.loading(false);

                                    Dialog.alert(__('An Error occurred, please refresh the page and try again.')).catch(
                                        () => {}
                                    );

                                    if (typeof self.onError === 'function') {
                                        self.onError();
                                    }
                                });
                        }
                    );
                } else if (config.type === CaptchaResponseType.RECAPTCHA) {
                    // 先调用recaptha score
                    const { captchaToken, nextStep } = await recaptchaByScore(config.site_keys!, action);

                    Toast.loading(false);

                    if (captchaToken) {
                        self.captchaType = CaptchaType.RECAPTCHA_SCORE;

                        setCaptchaType(CaptchaType.RECAPTCHA_SCORE);

                        // recaptcha 验证成功，清除失败记录
                        clearRecaptchaFailureRecord();

                        $formutil.$setValues(
                            {
                                captcha_token: captchaToken
                            },
                            () => {
                                $formutil.$validate('captcha_token');
                                self.onSuccess?.();
                            }
                        );
                    } else {
                        safetySetLoading(false);

                        if (nextStep === CaptchaResponseType.GEETEST) {
                            initCaptcha(CaptchaResponseType.GEETEST4);
                        } else if (nextStep === CaptchaResponseType.RECAPTCHA_CHECKBOX) {
                            self.captchaType = CaptchaType.RECAPTCHA_CHECKBOX;

                            setCaptchaType(CaptchaType.RECAPTCHA_CHECKBOX);

                            // score 模式评分较低 需要做 checkbox 验证则返回checkboxsitekey
                            const siteKey = config.site_keys!.find(item => item.type === 'checkbox')?.site_key;

                            self.widgetId = await recaptchaByCheckbox(boxRef.current, {
                                sitekey: siteKey!,
                                action,
                                callback: async token => {
                                    const params = {
                                        recaptcha_site_key: siteKey,
                                        recaptcha_token: token,
                                        recaptcha_action: action
                                    };

                                    // recaptcha 验证成功，清除失败记录
                                    clearRecaptchaFailureRecord();

                                    try {
                                        // 获取到 recaptcha checkbox token 去验证token 是否有效
                                        const { data } = await http.post(API.OAUTH.verifyCaptcha(), params);

                                        if (data.captcha_token) {
                                            $formutil.$setValues(
                                                {
                                                    captcha_token: data.captcha_token
                                                },
                                                () => {
                                                    $formutil.$validate('captcha_token');
                                                    self.onSuccess?.();
                                                }
                                            );
                                        } else {
                                            reset();
                                        }
                                    } catch (error) {
                                        reset();

                                        Toast.show(error);
                                    }
                                },
                                'expired-callback': () => {
                                    clearForm();
                                },
                                'error-callback': () => {
                                    clearForm();
                                }
                            });
                        }
                    }
                }
            } catch (error) {
                errorFallback(error.message);

                // recaptcha相关错误处理
                if (Object.values(CaptchaErrorCode).includes(error.error_code)) {
                    recordRecaptchaFailure();

                    notify(error);
                }
            }
        };

        useImperativeHandle(ref, () => ({
            reset: () => {
                reset();
            },
            captchaType: self.captchaType
        }));

        useEffect(() => {
            initCaptcha();

            return () => {
                clearForm();

                self.captchaObj?.destroy();
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        return (
            <div className={classlist('captcha-root', { checkbox: captchaType === CaptchaType.RECAPTCHA_CHECKBOX })}>
                {loading && <Loading tip={__('初始化验证中...')} />}
                <div className="captcha-container" ref={boxRef}>
                    <Field
                        name="captcha_token"
                        required
                        $validators={{
                            required(value) {
                                return !!value || __('请先进行身份验证!');
                            }
                        }}
                        children={null}
                    />
                </div>
            </div>
        );
    }
);

export default connect(Captcha);
